import { createFileRoute, notFound } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';

import { ThreadNotFoundPanel } from '../-components/empty-states';
import { ThreadPanel } from '../-components/thread-panel';
import type { Id } from '@minty/shared/ids';
import { DetailsLoading } from '~/components/list-panel-states';
import { getPostHog } from '~/lib/posthog';
import { useTRPC } from '~/lib/trpc';

export const Route = createFileRoute('/_dashboard/mailbox/incoming/$threadId')({
  pendingComponent: DetailsLoading,
  loader: async ({ context, params, preload }) => {
    const threadId = params.threadId as Id<'emailThreads'>;

    const thread = await context.queryClient.ensureQueryData(
      context.trpc.emails.views.getIncomingThread.queryOptions({ threadId }),
    );

    // Only when there is something to mark: reopening a thread already read
    // would cost a write the server then ignores.
    if (!preload && thread && !thread.thread.isRead) {
      void context.trpcClient.emails.placement.markThreadRead
        .mutate({ threadId })
        .catch((error: unknown) => getPostHog()?.captureException(error));
    }
  },
  notFoundComponent: ThreadNotFoundPanel,
  component: IncomingThreadRoute,
});

function IncomingThreadRoute() {
  const trpc = useTRPC();
  const { threadId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { data: details } = useSuspenseQuery(
    trpc.emails.views.getIncomingThread.queryOptions({
      threadId: threadId as Id<'emailThreads'>,
    }),
  );

  if (details === null) {
    throw notFound();
  }

  return (
    <ThreadPanel
      key={threadId}
      details={details}
      onThreadMoved={() =>
        void navigate({ to: '/mailbox/incoming', search: true })
      }
    />
  );
}
